"use client";

import { QueryClient } from "@tanstack/react-query";

/**
 * Service to handle agency changes and invalidate all relevant queries
 * This ensures data is refreshed when users switch agencies
 */
class AgencyChangeService {
  private queryClient: QueryClient | null = null;
  private isListening = false;
  private invalidationTimeout: NodeJS.Timeout | null = null;
  private boundHandleAgencyChange: (() => void) | null = null;
  private boundHandleStorageChange: ((event: StorageEvent) => void) | null = null;

  /**
   * Initialize the service with a QueryClient instance
   */
  init(queryClient: QueryClient): void {
    this.queryClient = queryClient;
    this.startListening();
  }

  /**
   * Start listening for agency change events
   */
  private startListening(): void {
    if (this.isListening || typeof window === 'undefined') return;

    this.isListening = true;

    // Create bound functions once
    this.boundHandleAgencyChange = this.handleAgencyChange.bind(this);
    this.boundHandleStorageChange = this.handleStorageChange.bind(this);

    // Listen for agency change events from AgencyTokenManager
    window.addEventListener('insuracentral-agency-change', this.boundHandleAgencyChange);
    
    // Listen for storage changes (when agency is updated in another tab)
    window.addEventListener('storage', this.boundHandleStorageChange);

  }

  /**
   * Handle agency change events
   */
  private handleAgencyChange(): void {
    // Set flag to prevent infinite loops from useAgencyStatus
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('__handling_agency_change__', 'true');
    }

    // Debug: Log current agency context
    if (typeof window !== 'undefined') {
      const agencyData = sessionStorage.getItem('insuracentral_current_agency');
      const parsedData = agencyData ? JSON.parse(agencyData) : null;
    }

    // Debounce invalidation to prevent rapid successive calls
    this.debouncedInvalidation();
  }

  /**
   * Handle storage changes (cross-tab agency updates)
   */
  private handleStorageChange(event: StorageEvent): void {
    if (event.key === 'insuracentral_current_agency' && event.newValue) {
      this.debouncedInvalidation();
    }
  }

  /**
   * Debounced invalidation to prevent rapid successive calls
   */
  private debouncedInvalidation(): void {
    if (this.invalidationTimeout) {
      clearTimeout(this.invalidationTimeout);
    }
    
    this.invalidationTimeout = setTimeout(() => {
      this.invalidateAllQueries();
      this.invalidationTimeout = null;
    }, 100); // 100ms debounce
  }

  /**
   * Invalidate all queries that depend on agency context
   */
  private invalidateAllQueries(): void {
    if (!this.queryClient) {

      return;
    }

    try {
      // Use prefix-based invalidation to catch all queries with agency context
      const queryPrefixes = [
        'leads',
        'lead-stats', // Lead statistics
        'resources',
        'appointments',
        'reminders',
        'communications',
        'communication', // Communication stats (singular - different from communications)
        'agency-dashboard',
        'agencies', // Add agencies queries
        'leaderboard',
        'globalLeaderboard',
        'dashboard',
        'premiumSales', // Add premium sales queries
        'user',
        'unassignedLeads', // Lead assignment
        'agentWorkload', // Lead assignment
        'agencyStatus', // Agency status
        'fax', // Fax queries
        'voicemail', // Voicemail queries
        'sms', // SMS queries
        'calls' // Call queries
      ];

      // Invalidate queries (mark as stale) and refetch active ones
      // Don't remove queries to prevent loading states
      Promise.all([
        ...queryPrefixes.map(prefix =>
          this.queryClient!.invalidateQueries({
            queryKey: [prefix],
            refetchType: 'active' // Only refetch active queries
          })
        ),
        // Specifically invalidate dashboard queries
        this.queryClient!.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            return Array.isArray(key) && (
              (key[0] === 'premiumSales') ||
              (key[0] === 'dashboard')
            );
          },
          refetchType: 'active'
        })
      ]).then(() => {

        // Clear the handling flag after invalidation completes
        setTimeout(() => {
          if (typeof window !== 'undefined') {
            sessionStorage.removeItem('__handling_agency_change__');
          }
        }, 1000);
      }).catch(error => {

        // Clear flag even on error to prevent getting stuck
        if (typeof window !== 'undefined') {
          sessionStorage.removeItem('__handling_agency_change__');
        }
        // Don't re-throw to prevent infinite loops
      });

    } catch (error) {

      // Don't re-throw to prevent infinite loops
    }
  }

  /**
   * Manually trigger query invalidation (useful for testing)
   */
  public triggerInvalidation(): void {
    this.invalidateAllQueries();
  }

  /**
   * Clean up event listeners
   */
  destroy(): void {
    if (typeof window === 'undefined') return;

    // Clear any pending invalidation
    if (this.invalidationTimeout) {
      clearTimeout(this.invalidationTimeout);
      this.invalidationTimeout = null;
    }

    // Remove event listeners using the stored bound functions
    if (this.boundHandleAgencyChange) {
      window.removeEventListener('insuracentral-agency-change', this.boundHandleAgencyChange);
      this.boundHandleAgencyChange = null;
    }
    
    if (this.boundHandleStorageChange) {
      window.removeEventListener('storage', this.boundHandleStorageChange);
      this.boundHandleStorageChange = null;
    }
    
    this.isListening = false;
    this.queryClient = null;
  }
}

// Export singleton instance
export const agencyChangeService = new AgencyChangeService();
export default agencyChangeService;
